import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Handle,
  Position,
  type NodeProps,
  useEdges,
  useNodes,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { ImageLightbox } from '../ImageLightbox';
import { VideoPlayer } from './VideoPlayer';
import { Download, FileText, Video, Zap, Lock, Link, X, ChevronUp, Settings, Info, Copy, ArrowLeftRight, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  GenerationSettings,
  getImageModelsCache,
  getVideoModelsCache,
  getTextModelsCache,
  getDefaultSettingsForModel,
  useModels,
  getSettingsSummary,
  type GenerationSettingsData,
} from './shared/GenerationSettings';
import { useNodeResize } from './useNodeResize';
import { NODE_WARNING_COMPACT_CLASS } from './shared/nodeStyles';
import { useNotificationStore } from '../../../stores/notificationStore';
import { usePaywallStore } from '../../../stores/paywallStore';
import { useCreditStore } from '../../../stores/creditStore';

// Re-export for backward compatibility with other files that import from here
export { getImageModelsCache, getVideoModelsCache, getTextModelsCache };

const DEFAULT_IMAGE_MODEL_ID = 'gemini-2.5-flash-image';

// Extended data type for GenerateNode
interface GenerateNodeDataProps {
  status?: 'idle' | 'generating' | 'complete' | 'error';
  imageUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  textContent?: string; // NEW: generated text content
  error?: string;
  mediaType?: 'image' | 'video' | 'text';
  inlineSettings?: GenerationSettingsData;
  canvasId?: string;
  is_template?: boolean; // Whether this node is a template source
  onUpdate?: (id: string, updates: any) => void;
  onGenerate?: (id: string) => void;
  onStopGeneration?: (id: string) => void;
  onDelete?: (id: string) => void;
  onConvertToPreset?: (id: string, settings: GenerationSettingsData) => void;
  onAddVariation?: (id: string) => void;
  onSaveAsTemplate?: (id: string) => void;
}

export function GenerateNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as GenerateNodeDataProps;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showInlineSettings, setShowInlineSettings] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const edges = useEdges();
  const nodes = useNodes();
  const updateNodeInternals = useUpdateNodeInternals();
  const { imageModels, videoModels, textModels } = useModels();
  const notify = useNotificationStore();
  const status = data.status || 'idle';
  const isGenerating = status === 'generating';

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: (rawData as any).nodeWidth as number | undefined,
    minWidth: 200,
    maxWidth: 600,
    onWidthChange: (w) => (rawData as any).onWidthChange?.(id, w),
  });

  // Check if a preset is connected
  const connectedPresetInfo = useMemo(() => {
    const incomingEdges = edges.filter((e) => e.target === id);
    const sourceNodeIds = incomingEdges.map((e) => e.source);
    const sourceNodes = nodes.filter((n) => sourceNodeIds.includes(n.id));
    const presetNode = sourceNodes.find((n) => n.type === 'preset');

    if (presetNode) {
      return {
        hasPreset: true,
        mediaType: (presetNode.data?.mediaType as 'image' | 'video' | 'text') || 'image',
        modelId: presetNode.data?.model as string | undefined,
        presetData: presetNode.data,
      };
    }
    return {
      hasPreset: false,
      mediaType: (data.inlineSettings?.mediaType as 'image' | 'video' | 'text') || 'image',
      modelId: data.inlineSettings?.model as string | undefined,
      presetData: null,
    };
  }, [id, edges, nodes, data.inlineSettings]);

  const hasConnectedPreset = connectedPresetInfo.hasPreset;
  const mediaType = connectedPresetInfo.mediaType;
  const isVideo = mediaType === 'video';
  const isText = mediaType === 'text';

  // Get effective settings (from preset or inline)
  const effectiveSettings = useMemo((): GenerationSettingsData | null => {
    if (hasConnectedPreset && connectedPresetInfo.presetData) {
      const pd = connectedPresetInfo.presetData as Record<string, unknown>;
      const model = pd.model as string | undefined;
      if (!model) return null; // No model selected yet
      return {
        mediaType: (pd.mediaType as 'image' | 'video' | 'text') || 'image',
        model,
        size: pd.size as string | undefined,
        aspectRatio: pd.aspectRatio as string | undefined,
        duration: pd.duration as string | undefined,
        resolution: pd.resolution as string | undefined,
        modelSettings: pd.modelSettings as Record<string, any> | undefined,
      };
    }
    return data.inlineSettings || null;
  }, [hasConnectedPreset, connectedPresetInfo.presetData, data.inlineSettings]);

  // When mediaType or status changes, node dimensions change —
  // tell ReactFlow to recalculate handle positions.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        updateNodeInternals(id);
      }, 50);
    });
    return () => cancelAnimationFrame(raf);
  }, [id, mediaType, data.status, showInlineSettings, data.textContent, updateNodeInternals]);

  // Compute connected image count and model limits (for image/video modes)
  const refImageInfo = useMemo(() => {
    const incomingEdges = edges.filter((e) => e.target === id);
    const sourceNodeIds = incomingEdges.map((e) => e.source);
    const sourceNodes = nodes.filter((n) => sourceNodeIds.includes(n.id));

    const imageNodes = sourceNodes.filter(
      (n) =>
        n.type === 'image' || n.type === 'upload' || (n.type === 'generate' && n.data?.imageUrl),
    );

    const modelId = connectedPresetInfo.modelId;
    let modelDef: any = null;

    if (isVideo) {
      const videoModels = getVideoModelsCache();
      modelDef = videoModels?.find((m: any) => m.id === modelId);
    } else if (!isText) {
      const imageModels = getImageModelsCache();
      modelDef = imageModels?.find((m: any) => m.id === modelId);
    }

    return {
      connectedImages: imageNodes.length,
      maxImages: modelDef?.maxReferenceImages ?? null,
      supportsRef: modelDef?.supportsReferenceImages ?? true,
      modelName: modelDef?.name || null,
      requiresRef: modelDef?.requiresReferenceImage ?? false,
    };
  }, [id, edges, nodes, connectedPresetInfo.modelId, isVideo, isText]);

  const handleGenerate = () => {
    if (data.onGenerate && !isGenerating) {
      data.onGenerate(id);
    }
  };

  // Handle inline settings changes
  const handleInlineSettingsChange = useCallback(
    (newSettings: GenerationSettingsData) => {
      if (data.onUpdate) {
        data.onUpdate(id, { data: { ...data, inlineSettings: newSettings } });
      }
    },
    [id, data],
  );

  // Handle text content edits
  const handleTextContentChange = useCallback(
    (newText: string) => {
      if (data.onUpdate) {
        data.onUpdate(id, { data: { ...data, textContent: newText } });
      }
    },
    [id, data],
  );

  // Handle media download via backend proxy (avoids CORS issues)
  const handleDownload = useCallback(async () => {
    if (!data.canvasId) {
      notify.error('Download failed: missing canvas context');
      return;
    }
    if (isDownloading) return;

    setIsDownloading(true);
    const isVideo = !!data.videoUrl;

    try {
      // Use fetch with credentials to include auth cookies and org header
      const downloadUrl = `/api/canvases/${data.canvasId}/nodes/${id}/download`;

      // Get org ID from localStorage (same as other API calls)
      const orgId = localStorage.getItem('ima_active_org_id');

      const response = await fetch(downloadUrl, {
        credentials: 'include',
        headers: orgId ? { 'X-Organization-Id': orgId } : {},
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Download failed');
      }

      // Get filename from Content-Disposition header
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="(.+)"/);
      const filename = filenameMatch?.[1] || `download-${Date.now()}`;

      // Create blob and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      notify.success(`${isVideo ? 'Video' : 'Image'} downloaded`);
    } catch (error: any) {
      console.error('Download failed:', error);
      notify.error(error.message || 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  }, [data.canvasId, data.videoUrl, id, isDownloading, notify]);

  // Auto-initialize inline settings when no preset is connected and models are loaded
  useEffect(() => {
    if (!hasConnectedPreset && !data.inlineSettings && imageModels.length > 0) {
      const defaultModel =
        imageModels.find((model) => model.id === DEFAULT_IMAGE_MODEL_ID) || imageModels[0];
      if (defaultModel) {
        const defaults = getDefaultSettingsForModel(defaultModel, 'image');
        handleInlineSettingsChange(defaults);
      }
      setShowInlineSettings(true);
    }
  }, [hasConnectedPreset, imageModels.length]);

  // Convert inline settings to a new preset node
  const handleConvertToPreset = useCallback(() => {
    if (!data.onConvertToPreset) return;

    // Use existing inline settings or create defaults from current model selection
    let settingsToConvert = data.inlineSettings;
    if (!settingsToConvert) {
      // If no inline settings yet, create defaults
      const defaultModel = imageModels[0];
      if (defaultModel) {
        settingsToConvert = getDefaultSettingsForModel(defaultModel, 'image');
      } else {
        // Fallback if models not loaded
        settingsToConvert = {
          mediaType: 'image',
          model: DEFAULT_IMAGE_MODEL_ID,
          size: '1K',
          aspectRatio: '1:1',
        };
      }
    }

    data.onConvertToPreset(id, settingsToConvert);
  }, [id, data, imageModels]);

  // Determine colors based on media type
  const headerBg = isText
    ? 'bg-teal-50 border-teal-100'
    : isVideo
      ? 'bg-purple-50 border-purple-100'
      : 'bg-primary-50 border-primary-100';
  const headerText = isText ? 'text-teal-700' : isVideo ? 'text-purple-700' : 'text-primary-700';
  const headerIcon = isText ? 'text-teal-600' : isVideo ? 'text-purple-600' : 'text-primary-600';
  const handleColor = isText ? '!bg-teal-500' : isVideo ? '!bg-purple-500' : '!bg-primary-500';

  const hasOutput = status === 'complete' && (data.imageUrl || data.videoUrl || data.textContent);
  const canGenerate = hasConnectedPreset || data.inlineSettings?.model;

  // Settings summary for display
  const settingsSummary = effectiveSettings
    ? getSettingsSummary(effectiveSettings, imageModels, videoModels, textModels)
    : null;

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-md min-w-[200px] relative ${!nodeWidth ? 'max-w-[280px]' : ''} ${selected ? 'border-primary-500' : status === 'error' ? 'border-error-300' : 'border-gray-200'}`}
      style={nodeWidth ? { width: nodeWidth } : undefined}
    >
      <div
        className={`px-3 py-2 border-b rounded-t-lg flex items-center gap-2 ${status === 'error' ? 'bg-error-50 border-error-100' : headerBg}`}
      >
        {isText ? (
          <FileText className={`w-4 h-4 ${status === 'error' ? 'text-error-600' : headerIcon} ${isGenerating ? 'animate-pulse' : ''}`} />
        ) : isVideo ? (
          <Video className={`w-4 h-4 ${status === 'error' ? 'text-error-600' : headerIcon} ${isGenerating ? 'animate-pulse' : ''}`} />
        ) : (
          <Zap className={`w-4 h-4 ${status === 'error' ? 'text-error-600' : headerIcon} ${isGenerating ? 'animate-spin' : ''}`} />
        )}
        <span
          className={`text-xs font-semibold ${status === 'error' ? 'text-error-700' : headerText}`}
        >
          Generate
        </span>
        {/* Mode indicator */}
        {hasConnectedPreset ? (
          <span className="text-[10px] px-1.5 py-px rounded-md leading-none bg-info-100 text-info-600 font-semibold">
            Preset
          </span>
        ) : data.inlineSettings ? (
          <span className="text-[10px] px-1.5 py-px rounded-md leading-none bg-gray-100 text-gray-600 font-semibold">
            Inline
          </span>
        ) : null}
        {isText && status !== 'error' && (
          <span className="text-[10px] px-1.5 py-px rounded-md leading-none bg-teal-100 text-teal-600 font-semibold">
            Text
          </span>
        )}
        {isVideo && status !== 'error' && (
          <span className="text-[10px] px-1.5 py-px rounded-md leading-none bg-purple-100 text-purple-600 font-semibold">
            Video
          </span>
        )}
        {/* Template lock indicator */}
        {data.is_template && (
          <span
            className="text-[10px] px-1.5 py-px rounded-md leading-none bg-indigo-100 text-indigo-600 font-semibold flex items-center gap-0.5"
            title="This node is used as a template source"
          >
            <Lock className="w-2.5 h-2.5" />
            Template
          </span>
        )}
        {/* Action buttons when selected */}
        {selected && (
          <div className="ml-auto flex items-center gap-1">
            {/* Save as Template button */}
            {data.onSaveAsTemplate && !data.is_template && (
              <button
                onClick={(e) => { e.stopPropagation(); data.onSaveAsTemplate?.(id); }}
                className="text-indigo-400 hover:text-indigo-600 p-0.5"
                aria-label="Save as Template"
                title="Save as Template"
              >
                <Link className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Delete button (disabled when is_template) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (data.is_template) {
                  notify.warning('Cannot delete: This node is used as a template source');
                  return;
                }
                data.onDelete?.(id);
              }}
              className={`p-0.5 ${data.is_template ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-500'}`}
              aria-label="Delete node"
              title={data.is_template ? 'Cannot delete: Used as template source' : 'Delete node'}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="p-3">
        {/* Complete state - show output */}
        {hasOutput ? (
          <div>
            {data.textContent ? (
              // TEXT OUTPUT - editable textarea
              <div className="mb-2">
                <textarea
                  className="w-full min-h-[120px] max-h-[300px] text-sm p-2 border border-gray-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-400 nodrag"
                  value={data.textContent}
                  onChange={(e) => handleTextContentChange(e.target.value)}
                  placeholder="Generated text will appear here..."
                />
                <div className="text-[10px] text-gray-400 mt-1 font-mono">
                  {data.textContent.length} characters
                </div>
              </div>
            ) : data.videoUrl ? (
              <div className="relative group">
                <VideoPlayer
                  videoUrl={data.videoUrl}
                  thumbnailUrl={data.thumbnailUrl}
                  className="w-full rounded-md mb-2"
                  compact
                />
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className={`absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-md text-white transition-opacity ${isDownloading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title={isDownloading ? 'Downloading...' : 'Download video'}
                >
                  {isDownloading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            ) : data.imageUrl ? (
              <div className="relative group">
                <img
                  src={data.imageUrl}
                  alt="Generated"
                  className="w-full h-auto rounded-md mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setLightboxOpen(true)}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  disabled={isDownloading}
                  className={`absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-md text-white transition-opacity ${isDownloading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title={isDownloading ? 'Downloading...' : 'Download image'}
                >
                  {isDownloading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            ) : null}

            {/* Settings section for regeneration - only show when no preset connected */}
            {!hasConnectedPreset && (
              <div className="mb-2">
                {showInlineSettings ? (
                  <div className="border border-gray-200 rounded-md p-2 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold text-gray-600">Settings</span>
                      <button
                        onClick={() => setShowInlineSettings(false)}
                        className="text-gray-400 hover:text-gray-600 p-0.5"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <GenerationSettings
                      settings={data.inlineSettings || { mediaType: 'image', model: '' }}
                      onChange={handleInlineSettingsChange}
                      compact={true}
                      showMediaTypeToggle={true}
                      connectedRefImageCount={refImageInfo.connectedImages}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setShowInlineSettings(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    {settingsSummary || 'Edit Settings'}
                  </button>
                )}
              </div>
            )}

            {/* Show preset indicator when connected */}
            {hasConnectedPreset && settingsSummary && (
              <div className="mb-2 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 flex items-center gap-1.5">
                <Info className="w-3 h-3 text-info-500" />
                <span>{settingsSummary}</span>
              </div>
            )}

            <Button
              variant="outline"
              size="xs"
              fullWidth
              onClick={handleGenerate}
            >
              <RefreshCw className="w-3 h-3" /> Regenerate
            </Button>
            {data.onAddVariation && (
              <Button
                variant="outline"
                size="xs"
                fullWidth
                onClick={() => data.onAddVariation!(id)}
                className="mt-1"
                leftIcon={<Copy className="w-3.5 h-3.5" />}
              >
                Add Variation
              </Button>
            )}
          </div>
        ) : status === 'generating' ? (
          <div className="flex flex-col items-center justify-center h-[80px]">
            <Spinner size="md" className="mb-2" />
            <span className="text-xs text-gray-500">
              {isText ? 'Generating text...' : isVideo ? 'Generating video...' : 'Generating...'}
            </span>
            {isVideo && (
              <span className="text-[10px] text-gray-400 mt-1">This may take a minute</span>
            )}
            {data.onStopGeneration && (
              <button
                onClick={() => data.onStopGeneration!(id)}
                className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Stop
              </button>
            )}
          </div>
        ) : status === 'error' ? (
          (() => {
            const isBillingErr = data.error?.includes('usage limit exceeded') || data.error?.includes('No active subscription') || data.error?.includes('Insufficient credits') || data.error?.includes('No credits available');
            if (isBillingErr) {
              // Determine which scenario we're in using credit store context
              const creditBalance = useCreditStore.getState().balance;
              const subStatus = creditBalance?.subscriptionStatus;
              const cancelAtEnd = creditBalance?.cancelAtPeriodEnd;
              const isNoSub = data.error?.includes('No active subscription');

              let label: string;
              let ctaText: string;
              let variant: string;

              if (isNoSub && !subStatus) {
                // Scenario A or E: no real subscription
                label = 'Subscription Required';
                ctaText = 'Choose a Plan';
                variant = 'upgrade';
              } else if (subStatus === 'trialing' && cancelAtEnd) {
                // Scenario B: cancelled trial
                label = 'Trial Cancelled';
                ctaText = 'View Plans';
                variant = 'cancelled_trial';
              } else if (subStatus === 'trialing' && !cancelAtEnd) {
                // Scenario C: exhausted trial
                label = 'Credits Used Up';
                ctaText = 'Start Plan Now';
                variant = 'exhausted_trial';
              } else if (subStatus === 'active') {
                // Scenario D: paid active, out of credits
                label = 'Out of Credits';
                ctaText = 'View Plans';
                variant = 'exhausted';
              } else {
                // Fallback (E: expired/cancelled)
                label = 'Subscription Required';
                ctaText = 'Choose a Plan';
                variant = 'upgrade';
              }

              return (
                <div className="text-center py-1.5">
                  <div className="w-8 h-8 mx-auto mb-1.5 rounded-full bg-warning-50 border border-warning-200 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-warning-500" />
                  </div>
                  <p className="text-[11px] font-semibold text-gray-800 mb-0.5 leading-tight">
                    {label}
                  </p>
                  <p className="text-[10px] text-gray-500 leading-snug mb-2">
                    {variant === 'exhausted_trial' ? 'Start your paid plan to get fresh credits.'
                      : variant === 'cancelled_trial' ? 'Subscribe to continue creating.'
                      : variant === 'exhausted' ? 'Credits used up. Upgrade or wait for next cycle.'
                      : 'Choose a plan to start creating.'}
                  </p>
                  <Button
                    variant="primary"
                    size="xs"
                    fullWidth
                    onClick={() => {
                      usePaywallStore.getState().openPaywall('generate-node', variant as any);
                    }}
                  >
                    {ctaText}
                  </Button>
                </div>
              );
            }
            return (
              <div>
                <p className="text-xs text-error-600 mb-2 text-center">{data.error || 'Generation failed'}</p>

                {/* Settings section for retry - only show when no preset connected */}
                {!hasConnectedPreset && (
                  <div className="mb-2">
                    {showInlineSettings ? (
                      <div className="border border-gray-200 rounded-md p-2 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-semibold text-gray-600">Settings</span>
                          <button
                            onClick={() => setShowInlineSettings(false)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <GenerationSettings
                          settings={data.inlineSettings || { mediaType: 'image', model: '' }}
                          onChange={handleInlineSettingsChange}
                          compact={true}
                          showMediaTypeToggle={true}
                        />
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowInlineSettings(true)}
                        className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        {settingsSummary || 'Change Settings'}
                      </button>
                    )}
                  </div>
                )}

                {/* Show preset indicator when connected */}
                {hasConnectedPreset && settingsSummary && (
                  <div className="mb-2 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 flex items-center gap-1.5">
                    <Info className="w-3 h-3 text-info-500" />
                    <span>{settingsSummary}</span>
                  </div>
                )}

                <Button
                  variant="danger"
                  size="xs"
                  fullWidth
                  onClick={handleGenerate}
                >
                  Retry
                </Button>
              </div>
            );
          })()
        ) : (
          <div>
            {/* Show settings summary when configured */}
            {settingsSummary && !showInlineSettings && (
              <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-md mb-2 flex items-center justify-between">
                <span>{settingsSummary}</span>
                {!hasConnectedPreset && (
                  <button
                    onClick={() => setShowInlineSettings(true)}
                    className="text-primary-600 hover:text-primary-700 ml-2"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Inline settings panel */}
            {!hasConnectedPreset && showInlineSettings && (
              <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold text-gray-600">Quick Settings</span>
                  <button
                    onClick={() => setShowInlineSettings(false)}
                    className="text-gray-400 hover:text-gray-600 p-0.5"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                </div>
                <GenerationSettings
                  settings={data.inlineSettings || { mediaType: 'image', model: '' }}
                  onChange={handleInlineSettingsChange}
                  compact={true}
                  showMediaTypeToggle={true}
                />
                {/* Convert to Preset button */}
                {data.inlineSettings && data.onConvertToPreset && (
                  <button
                    onClick={handleConvertToPreset}
                    className="w-full mt-2 text-xs px-2 py-1 text-info-600 hover:text-info-700 hover:bg-info-50 rounded-md border border-info-200 flex items-center justify-center gap-1"
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                    Extract to Preset Node
                  </button>
                )}
              </div>
            )}

            {/* Missing ref image warning for models that require it (not for text mode) */}
            {!isText && refImageInfo.requiresRef && refImageInfo.connectedImages === 0 && (
              <div className={`${NODE_WARNING_COMPACT_CLASS} mb-2 flex items-start gap-1.5`}>
                <AlertTriangle className="w-3.5 h-3.5 text-warning-500 flex-shrink-0 mt-px" />
                <span>{refImageInfo.modelName || 'This model'} requires a reference image</span>
              </div>
            )}

            <Button
              variant="primary"
              size="xs"
              fullWidth
              onClick={handleGenerate}
              disabled={!canGenerate}
              leftIcon={
                isText ? (
                  <FileText className="w-3.5 h-3.5" />
                ) : isVideo ? (
                  <Video className="w-3.5 h-3.5" />
                ) : (
                  <Zap className="w-3.5 h-3.5" />
                )
              }
            >
              {isText ? 'Generate Text' : isVideo ? 'Generate Video' : 'Generate Image'}
            </Button>
          </div>
        )}
      </div>

      {/* Reference image limit warnings (not for text mode) */}
      {!isText &&
        refImageInfo.connectedImages > 0 &&
        !refImageInfo.supportsRef &&
        refImageInfo.modelName && (
          <div className="px-3 pb-2">
            <div className={`${NODE_WARNING_COMPACT_CLASS} flex items-start gap-1.5`}>
              <AlertTriangle className="w-3.5 h-3.5 text-warning-500 flex-shrink-0 mt-px" />
              <span>{refImageInfo.modelName} doesn't support reference images —{' '}
              {refImageInfo.connectedImages} connected image(s) will be ignored</span>
            </div>
          </div>
        )}
      {!isText &&
        refImageInfo.connectedImages > 0 &&
        refImageInfo.supportsRef &&
        refImageInfo.maxImages !== null &&
        refImageInfo.connectedImages > refImageInfo.maxImages && (
          <div className="px-3 pb-2">
            <div className={`${NODE_WARNING_COMPACT_CLASS} flex items-start gap-1.5`}>
              <AlertTriangle className="w-3.5 h-3.5 text-warning-500 flex-shrink-0 mt-px" />
              <span>{refImageInfo.modelName} supports max {refImageInfo.maxImages} image(s) — only first{' '}
              {refImageInfo.maxImages} of {refImageInfo.connectedImages} will be used</span>
            </div>
          </div>
        )}

      <Handle
        type="target"
        position={Position.Left}
        id="generate-target"
        className={`!w-3 !h-3 ${handleColor}`}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="generate-source"
        className={`!w-3 !h-3 ${handleColor}`}
      />
      <div {...resizeHandleProps} />
      {lightboxOpen && data.imageUrl && (
        <ImageLightbox
          src={data.imageUrl}
          alt="Generated image"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
